import { useRef, useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge, Switch } from '../../components/ui.tsx';
import { t, tn, type MessageKey } from '../../i18n/index.ts';
import { action, upload } from '../../lib/api.ts';
import { day as dayText, money, parseMoney } from '../../lib/format.ts';
import { useSession } from '../../lib/session.tsx';
import { useUi } from '../../lib/ui-state.tsx';
import { useAction, useSave } from '../manage/common.tsx';
import { careersUrl, PayDialog } from '../People.tsx';
import { Avatar } from './Directory.tsx';

/**
 * Referrals: everyone recommends friends for open positions, by a short form or by sharing their
 * own link to the careers page, and follows them in broad steps. The bonus, when the business sets
 * one, is owed once the person is hired and paid by whoever pays expenses back.
 */

type Step = 'received' | 'reviewing' | 'interview' | 'offer' | 'hired' | 'closed';
interface JobRow {
  id: string;
  title: string;
  department: string | null;
  kind: 'full_time' | 'part_time' | 'seasonal';
  description: string | null;
  closes: string | null;
  public: boolean;
  location: string | null;
  mine: number;
}
interface Mine {
  id: string;
  name: string;
  job: string;
  step: Step;
  created_at: string;
  stage_at: string;
  bonus_state: 'none' | 'due' | 'paid';
  bonus_amount: number | null;
  bonus_paid_at: string | null;
}
interface Due {
  id: string;
  name: string;
  job: string;
  referrer: string;
  referred_by: string;
  hired_at: string;
  bonus_amount: number;
}
interface Front {
  on: boolean;
  bonus: number | null;
  rules: string | null;
  careers: boolean;
  year: number;
  jobs: JobRow[];
  mine: Mine[];
  board: { id: string; name: string; referred: number; hired: number }[];
  due: Due[];
  can: { settings: boolean; pay: boolean };
}

const TRACK: Step[] = ['received', 'interview', 'offer', 'hired'];
const ACCEPT = 'application/pdf,image/jpeg,image/png';
const MAX_FILE = 8_000_000;
const readBase64 = (f: File) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(r.error);
    r.readAsDataURL(f);
  });

export function ReferTab({ onChange }: { onChange: () => Promise<void> }) {
  const { data, reload } = useAction<Front>('hr.referrals');
  const { tenant } = useSession();
  const { toast } = useUi();
  const [refer, setRefer] = useState<JobRow | null>(null);
  const [setup, setSetup] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [paying, setPaying] = useState(false);
  const token = useRef<string | null>(null);
  const changed = async () => {
    await reload();
    await onChange();
  };
  if (!data) return <Skeleton h={320} r="var(--radius-lg)" />;
  const hired = data.mine.filter((m) => m.step === 'hired').length;
  const share = async (j: JobRow) => {
    try {
      token.current ??= (await action<{ token: string }>('hr.referral_link')).token;
      const url = `${careersUrl(tenant?.slug ?? '')}?job=${j.id}&ref=${token.current}`;
      await navigator.clipboard.writeText(url);
      toast(t('ref.link_copied', { job: j.title }), 'ok');
    } catch (e) {
      toast((e as Error).message || t('ref.link_failed'), 'danger');
    }
  };
  const due = data.due;
  const pickedRows = due.filter((d) => picked.has(d.id));
  const pickedTotal = pickedRows.reduce((s, d) => s + d.bonus_amount, 0);
  return (
    <section className="panel ref-panel">
      <div className="ref-hero">
        <span className="ref-hero-icon">
          <Icon name="gift" size={26} />
        </span>
        <div className="ref-hero-text">
          <h2>{t('ref.hero_title')}</h2>
          <p>{data.on ? (data.bonus ? t('ref.hero_bonus', { amount: money(data.bonus) }) : t('ref.hero_plain')) : t('ref.off_body')}</p>
          {data.on && data.rules ? <p className="ref-rules small">{data.rules}</p> : null}
        </div>
        <div className="ref-hero-side">
          {data.mine.length ? (
            <span className="ref-score">
              <strong>{data.mine.length}</strong>
              <span className="small">{tn('ref.referred_n', data.mine.length)}</span>
            </span>
          ) : null}
          {hired ? (
            <span className="ref-score">
              <strong>{hired}</strong>
              <span className="small">{tn('ref.hired_n', hired)}</span>
            </span>
          ) : null}
          {data.can.settings ? (
            <Button size="sm" icon="settings" onClick={() => setSetup(true)}>
              {t('ref.settings')}
            </Button>
          ) : null}
        </div>
      </div>

      {due.length ? (
        <div className="ref-block">
          <div className="ref-block-head">
            <h3>{t('ref.due_title')}</h3>
            <span className="muted small">{t('ref.due_hint', { amount: money(due.reduce((s, d) => s + d.bonus_amount, 0)) })}</span>
            <Button variant="primary" size="sm" icon="cash" disabled={!pickedRows.length} onClick={() => setPaying(true)}>
              {pickedRows.length ? t('ref.pay_n', { amount: money(pickedTotal) }) : t('ref.pick_to_pay')}
            </Button>
          </div>
          <ul className="ref-due">
            {due.map((d) => (
              <li key={d.id}>
                <label className="ref-due-row">
                  <input
                    type="checkbox"
                    checked={picked.has(d.id)}
                    onChange={(e) =>
                      setPicked((p) => {
                        const n = new Set(p);
                        if (e.target.checked) n.add(d.id);
                        else n.delete(d.id);
                        return n;
                      })
                    }
                  />
                  <Avatar name={d.referrer} photo={null} size="sm" />
                  <span className="ref-due-text">
                    <strong>{d.referrer}</strong>
                    <span className="muted small">{t('ref.due_for', { name: d.name, job: d.job, day: dayText(d.hired_at.slice(0, 10)) })}</span>
                  </span>
                  <strong className="ref-amount">{money(d.bonus_amount)}</strong>
                </label>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {data.on ? (
        <div className="ref-block">
          <div className="ref-block-head">
            <h3>{t('ref.open_title')}</h3>
            <span className="muted small">{data.jobs.length ? tn('ref.open_n', data.jobs.length) : null}</span>
          </div>
          {data.jobs.length ? (
            <ul className="ref-jobs">
              {data.jobs.map((j) => (
                <li key={j.id} className="ref-job">
                  <div className="ref-job-top">
                    <span className="careers-kind">{t(`careers.kind_${j.kind}` as MessageKey)}</span>
                    {j.mine ? <StatusBadge tone="info" icon="gift" label={tn('ref.you_referred', j.mine)} /> : null}
                  </div>
                  <strong className="ref-job-title">{j.title}</strong>
                  {j.department || j.location ? <span className="muted small">{[j.department, j.location].filter(Boolean).join(' · ')}</span> : null}
                  {j.description ? <p className="small ref-job-desc">{j.description}</p> : null}
                  {j.closes ? <span className="muted small">{t('careers.apply_by', { day: dayText(j.closes) })}</span> : null}
                  <div className="ref-job-actions">
                    <Button variant="primary" size="sm" icon="plus" onClick={() => setRefer(j)}>
                      {t('ref.refer')}
                    </Button>
                    {data.careers && j.public ? (
                      <Button size="sm" icon="share" onClick={() => void share(j)} aria-label={t('ref.share_aria', { job: j.title })}>
                        {t('ref.share')}
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon="users" title={t('ref.no_jobs')} body={t('ref.no_jobs_body')} />
          )}
        </div>
      ) : (
        <EmptyState
          icon="gift"
          title={t('ref.off_title')}
          body={data.can.settings ? t('ref.off_body_manage') : t('ref.off_body')}
          action={
            data.can.settings ? (
              <Button variant="primary" icon="settings" onClick={() => setSetup(true)}>
                {t('ref.settings')}
              </Button>
            ) : undefined
          }
        />
      )}

      <div className="ref-columns">
        <div className="ref-block">
          <div className="ref-block-head">
            <h3>{t('ref.mine_title')}</h3>
          </div>
          {data.mine.length ? (
            <ul className="ref-mine">
              {data.mine.map((m) => (
                <MineRow key={m.id} m={m} />
              ))}
            </ul>
          ) : (
            <p className="muted small ref-empty">{t('ref.mine_empty')}</p>
          )}
        </div>
        {data.board.length ? (
          <div className="ref-block ref-board">
            <div className="ref-block-head">
              <h3>{t('ref.board_title', { year: data.year })}</h3>
            </div>
            <ol>
              {data.board.map((b, i) => (
                <li key={b.id}>
                  <span className={`ref-rank${i === 0 ? ' first' : ''}`}>{i + 1}</span>
                  <Avatar name={b.name} photo={null} size="sm" />
                  <span className="ref-board-text">
                    <strong>{b.name}</strong>
                    <span className="muted small">{t('ref.board_counts', { referred: b.referred, hired: b.hired })}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        ) : null}
      </div>

      {refer ? (
        <ReferDialog
          job={refer}
          onClose={() => setRefer(null)}
          onSaved={async () => {
            setRefer(null);
            await changed();
          }}
        />
      ) : null}
      {setup ? (
        <SettingsDialog
          initial={data}
          onClose={() => setSetup(false)}
          onSaved={async () => {
            setSetup(false);
            await changed();
          }}
        />
      ) : null}
      {paying ? (
        <PayDialog
          count={pickedRows.length}
          total={pickedTotal}
          title={t('ref.pay_title', { amount: money(pickedTotal) })}
          body={tn('ref.pay_body', pickedRows.length)}
          toast={tn('ref.paid_toast', pickedRows.length, { amount: money(pickedTotal) })}
          cta={t('ref.mark_paid')}
          onClose={() => setPaying(false)}
          onPay={async (method, reference) => {
            await action('hr.pay_referral_bonuses', { ids: pickedRows.map((d) => d.id), method, reference });
            setPaying(false);
            setPicked(new Set());
            await changed();
          }}
        />
      ) : null}
    </section>
  );
}

function MineRow({ m }: { m: Mine }) {
  const closed = m.step === 'closed';
  const at = closed ? -1 : m.step === 'reviewing' ? 0 : TRACK.indexOf(m.step);
  return (
    <li className="ref-mine-row">
      <Avatar name={m.name} photo={null} size="md" />
      <div className="ref-mine-text">
        <strong>{m.name}</strong>
        <span className="muted small">{t('ref.mine_meta', { job: m.job, day: dayText(m.created_at.slice(0, 10)) })}</span>
      </div>
      <div className="ref-mine-status">
        {closed ? (
          <StatusBadge tone="neutral" icon="x" label={t('ref.step_closed')} />
        ) : (
          <>
            <ol className="ref-track" aria-label={t('ref.step_aria', { step: t(`ref.step_${m.step}` as MessageKey) })}>
              {TRACK.map((s, i) => (
                <li key={s} className={i < at ? 'done' : i === at ? 'now' : ''} title={t(`ref.step_${s}` as MessageKey)} />
              ))}
            </ol>
            <span className={`small ${m.step === 'hired' ? 'text-ok' : 'muted'}`}>{t(`ref.step_${m.step}` as MessageKey)}</span>
          </>
        )}
        {m.bonus_state === 'due' && m.bonus_amount ? <StatusBadge tone="warn" icon="clock" label={t('ref.bonus_due', { amount: money(m.bonus_amount) })} /> : null}
        {m.bonus_state === 'paid' && m.bonus_amount ? <StatusBadge tone="ok" icon="cash" label={t('ref.bonus_paid', { amount: money(m.bonus_amount) })} /> : null}
      </div>
    </li>
  );
}

function ReferDialog({ job, onClose, onSaved }: { job: JobRow; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({ name: '', email: '', phone: '', note: '', consent: false });
  const [file, setFile] = useState<File | null>(null);
  const [tried, setTried] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const emailBad = !!f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim());
  const reach = !!f.email.trim() || !!f.phone.trim();
  const errors = {
    name: f.name.trim().length < 2 ? t('ref.e_name') : null,
    email: emailBad ? t('ref.e_email') : !reach ? t('ref.e_reach') : null,
    file: file && file.size > MAX_FILE ? t('ref.e_file') : null,
    consent: !f.consent ? t('ref.e_consent') : null,
  };
  const submit = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(
      async () => {
        await upload('hr.refer', {
          job: job.id,
          name: f.name.trim(),
          email: f.email.trim() || null,
          phone: f.phone.trim() || null,
          note: f.note.trim() || null,
          cv: file ? { name: file.name, data: await readBase64(file) } : null,
          consent: true,
        });
        await onSaved();
      },
      t('ref.sent', { name: f.name.trim() }),
    );
  };
  return (
    <Dialog
      title={t('ref.refer_for', { job: job.title })}
      onClose={onClose}
      size="md"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="send" busy={busy} onClick={submit}>
            {t('ref.send')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <p className="muted small">{t('ref.form_hint')}</p>
        <Field label={t('ref.their_name')} error={tried ? errors.name : null} required>
          <input className="input" value={f.name} maxLength={120} onChange={(e) => set('name', e.target.value)} data-autofocus autoComplete="off" />
        </Field>
        <div className="hr-two">
          <Field label={t('ref.their_email')} error={tried ? errors.email : null}>
            <input className="input" type="email" inputMode="email" value={f.email} maxLength={160} onChange={(e) => set('email', e.target.value)} autoComplete="off" />
          </Field>
          <Field label={t('ref.their_phone')} hint={t('ref.reach_hint')}>
            <input className="input" type="tel" inputMode="tel" value={f.phone} maxLength={40} onChange={(e) => set('phone', e.target.value)} autoComplete="off" />
          </Field>
        </div>
        <Field label={t('ref.why')} hint={t('ref.why_hint')}>
          <textarea className="input" rows={3} maxLength={2000} value={f.note} onChange={(e) => set('note', e.target.value)} />
        </Field>
        <Field label={t('ref.cv')} error={tried ? errors.file : null} hint={t('ref.cv_hint')}>
          <div className="ref-file">
            <input ref={input} type="file" accept={ACCEPT} hidden onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            <Button size="sm" icon="upload" onClick={() => input.current?.click()}>
              {file ? t('ref.cv_change') : t('ref.cv_add')}
            </Button>
            {file ? (
              <span className="small ref-file-name">
                <Icon name="file" size={14} /> {file.name}
                <button type="button" className="link-button small" onClick={() => setFile(null)}>
                  {t('ref.cv_remove')}
                </button>
              </span>
            ) : null}
          </div>
        </Field>
        <label className="ref-consent">
          <input type="checkbox" checked={f.consent} onChange={(e) => set('consent', e.target.checked)} />
          <span>{t('ref.consent')}</span>
        </label>
        {tried && errors.consent ? (
          <span className="field-msg" role="alert">
            {errors.consent}
          </span>
        ) : null}
      </div>
    </Dialog>
  );
}

function SettingsDialog({ initial, onClose, onSaved }: { initial: Front; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [on, setOn] = useState(initial.on);
  const [bonus, setBonus] = useState(initial.bonus ? String(initial.bonus / 100) : '');
  const [rules, setRules] = useState(initial.rules ?? '');
  const [tried, setTried] = useState(false);
  const amount = bonus.trim() ? parseMoney(bonus) : null;
  const bad = bonus.trim() !== '' && (amount === null || amount < 0);
  return (
    <Dialog
      title={t('ref.settings_title')}
      onClose={onClose}
      size="md"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (bad) return;
              void run(async () => {
                await action('hr.save_referral_settings', { referrals: on, referral_bonus: amount, referral_rules: rules.trim() || null });
                await onSaved();
              }, t('ref.settings_saved'));
            }}
          >
            {t('common.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <div className="setting-row">
          <div>
            <strong>{t('ref.switch')}</strong>
            <p className="muted">{t('ref.switch_hint')}</p>
          </div>
          <Switch checked={on} onChange={setOn} label={t('ref.switch')} />
        </div>
        <Field label={t('ref.bonus')} hint={t('ref.bonus_hint')} error={tried && bad ? t('ref.e_bonus') : null}>
          <input className="input" inputMode="decimal" value={bonus} onChange={(e) => setBonus(e.target.value)} placeholder={t('ref.bonus_none')} />
        </Field>
        <Field label={t('ref.rules')} hint={t('ref.rules_hint')}>
          <textarea className="input" rows={4} maxLength={3000} value={rules} onChange={(e) => setRules(e.target.value)} />
        </Field>
        <p className="small muted">{t('hr.pay_books_hint')}</p>
      </div>
    </Dialog>
  );
}
